/**
 * The approved-revision comparison for one programme.
 *
 * Two reads: the programme's governance record, which is the list of approved
 * revisions, and the diff of whichever pair is chosen. The second is disabled
 * until a pair exists, and a disabled `useAsync` parks at `loading` — so this
 * route reads its status rather than putting a boundary over it.
 *
 * @module router/portfolio/RevisionComparisonRoute
 */

import React, { useMemo, useState } from "react";

import { Navigate, useNavigate, useParams, useSearchParams } from "react-router";

import { ComparisonLoading } from "@/components/portfolio/comparison/ComparisonLoading";
import { RevisionComparison } from "@/components/portfolio/comparison/RevisionComparison";
import { AsyncBoundary, Button, EmptyState, useAsync } from "@/components/ui";
import type { ProgrammeGovernanceSummary } from "@/platform";
import { useWorkspaceOutletContext } from "@/router/workspace-context";
import type { CurriculumRevision } from "@/server/types";

/** The pair a fresh visit compares: the newest revision against the one before it. */
function defaultPair(
  revisions: readonly CurriculumRevision[],
  requestedToId: string | undefined,
): { fromRevisionId: string; toRevisionId: string } {
  const requested = revisions.find((revision) => revision.id === requestedToId);
  // A requested target at index 0 has nothing before it to compare against, so
  // the newest revision is used instead of showing an empty comparison.
  const target = requested && revisions.indexOf(requested) > 0 ? requested : revisions.at(-1);
  const targetIndex = target ? revisions.indexOf(target) : -1;
  return {
    fromRevisionId: targetIndex > 0 ? revisions[targetIndex - 1]!.id : "",
    toRevisionId: target?.id ?? "",
  };
}

export function RevisionComparisonRoute(): React.JSX.Element {
  const navigate = useNavigate();
  const { programmeId } = useParams();
  const [searchParams] = useSearchParams();
  const { client } = useWorkspaceOutletContext();
  const requestedToId = searchParams.get("to") ?? undefined;

  // What the reader chose, absent until they choose. The pair actually on
  // screen falls back to the default below, which is derived rather than
  // stored: seeding state from a read that has not answered yet leaves both
  // selects empty for ever, which is the defect the creation routes shipped.
  const [pickedFrom, setPickedFrom] = useState<string>();
  const [pickedTo, setPickedTo] = useState<string>();

  const governance = useAsync<ProgrammeGovernanceSummary>(
    () => client.getProgrammeGovernance(programmeId ?? ""),
    [client, programmeId],
    {
      enabled: Boolean(programmeId),
      // A single approved revision has nothing to compare against, which is
      // the screen's empty case rather than an error or a blank comparison.
      isEmpty: (data) => data.curriculumRevisions.length < 2,
    },
  );
  // Memoised because the seeding effect below depends on it: a fresh array
  // every render would re-run the effect on every render.
  const revisions = useMemo(
    () =>
      governance.status === "ready"
        ? [...governance.data.curriculumRevisions].sort(
            (left, right) => left.internalSequence - right.internalSequence,
          )
        : [],
    [governance],
  );

  const fallback = defaultPair(revisions, requestedToId);
  const fromRevisionId = pickedFrom ?? fallback.fromRevisionId;
  const toRevisionId = pickedTo ?? fallback.toRevisionId;

  const pairChosen = Boolean(fromRevisionId && toRevisionId && fromRevisionId !== toRevisionId);
  const comparison = useAsync(
    () =>
      client.compareGovernanceRevisions({
        programmeId: programmeId ?? "",
        fromRevisionId,
        toRevisionId,
      }),
    [client, programmeId, fromRevisionId, toRevisionId],
    { enabled: pairChosen },
  );

  if (!programmeId) {
    return <Navigate to="/portfolio" replace />;
  }

  const back = (): void => {
    navigate("/portfolio");
  };

  // Handled here rather than through the boundary's `error` render prop. The
  // boundary puts its one `data-testid` on both its loading state and its
  // error card, so the two cannot be told apart by the id each already has.
  if (governance.status === "error") {
    return (
      <EmptyState
        icon="warning"
        title="Unable to load curriculum revisions"
        description={governance.error.message}
        data-testid="revision-comparison-error"
        action={
          <Button
            variant="outline"
            onClick={() => {
              governance.reload().catch(() => undefined);
            }}
          >
            Try again
          </Button>
        }
      />
    );
  }

  return (
    <AsyncBoundary
      state={governance}
      loadingLabel="Loading curriculum revisions"
      loading={<ComparisonLoading />}
      data-testid="revision-comparison-loading"
      empty={
        <EmptyState
          icon="git-diff"
          title="No earlier approved version to compare"
          description="Version comparison becomes available after this programme has a second approved revision."
          action={
            <Button variant="ghost" icon="arrow-left" onClick={back}>
              Back to Portfolio
            </Button>
          }
          data-testid="revision-comparison-unavailable"
        />
      }
    >
      {() => (
        <RevisionComparison
          revisions={revisions}
          fromRevisionId={fromRevisionId}
          toRevisionId={toRevisionId}
          onFromChange={setPickedFrom}
          onToChange={setPickedTo}
          comparison={comparison.status === "ready" ? comparison.data : null}
          comparing={pairChosen && comparison.status === "loading"}
          errorMessage={comparison.status === "error" ? comparison.error.message : undefined}
          onBack={back}
          fallbackTitle={programmeId}
        />
      )}
    </AsyncBoundary>
  );
}
